import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { createHealthRecordServiceClient, type Appointment, type AppointmentInput, type AppointmentStatus } from "./client";

export type AppointmentsStatus = "idle" | "loading" | "ready" | "saving" | "error";

export function useAppointments() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.healthRecordService?.apiBaseUrl;
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [status, setStatus] = useState<AppointmentsStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createHealthRecordServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const loadAppointments = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Appointments are not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);
      setSaved(false);

      try {
        const records = await client.listAppointments(signal);
        setAppointments(records);
        setStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }

        setError(nextError instanceof Error ? nextError.message : "We could not load your appointments.");
        setStatus("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }

    const controller = new AbortController();
    void loadAppointments(controller.signal);

    return () => controller.abort();
  }, [auth.isAuthenticated, loadAppointments]);

  useEffect(() => {
    if (!saved) {
      return undefined;
    }

    const timeout = window.setTimeout(() => setSaved(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [saved]);

  const createAppointment = useCallback(
    async (payload: AppointmentInput) => {
      if (!client) {
        setError("Appointments are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = await client.createAppointment(payload);
        setAppointments((current) => [record, ...current]);
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save the appointment.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  const updateAppointment = useCallback(
    async (appointmentId: string, payload: Partial<AppointmentInput> | { status: AppointmentStatus }) => {
      if (!client) {
        setError("Appointments are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = await client.updateAppointment(appointmentId, payload);
        setAppointments((current) => current.map((item) => (item.appointment_id === appointmentId ? record : item)));
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save the appointment.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  const cancelAppointment = useCallback((appointmentId: string) => updateAppointment(appointmentId, { status: "cancelled" }), [updateAppointment]);
  const archiveAppointment = useCallback((appointmentId: string) => updateAppointment(appointmentId, { status: "archived" }), [updateAppointment]);

  return {
    appointments,
    archiveAppointment,
    cancelAppointment,
    createAppointment,
    error,
    loadAppointments,
    saved,
    status,
    updateAppointment,
  };
}
